/**
 * ASCII Art Generator - runtime content loader
 *
 * Provides the single runtime loading path used by export and download flows.
 */

import bundledRuntimeContent from '../../../public/ascii-bg-runtime.js?raw'

const RUNTIME_PATH = '/ascii-bg-runtime.js'
const RUNTIME_FILENAME = 'ascii-bg-runtime.js'
const RUNTIME_VERSION = '1.0.0'

function isHtmlFallback(content: string): boolean {
  const trimmedContent = content.trim().toLowerCase()
  return (
    trimmedContent.startsWith('<!doctype') ||
    trimmedContent.startsWith('<html') ||
    trimmedContent.startsWith('<script type="module">')
  )
}

function isRuntimeShapeValid(content: string): boolean {
  return content.includes('AsciiBgRuntime') || content.includes('window.AsciiBg')
}

function hasFullFeatureMarkers(content: string): boolean {
  return content.includes('EffectsProcessor') || content.includes('GlitchEffect')
}

async function fetchRuntimeAsset(): Promise<string | null> {
  try {
    const response = await fetch(RUNTIME_PATH)
    if (!response.ok) {
      console.warn('[runtime-content] Fetch failed, status:', response.status)
      return null
    }

    const content = await response.text()
    if (isHtmlFallback(content)) {
      console.warn('[runtime-content] Received HTML instead of JS - Vite fallback detected')
      return null
    }

    if (!isRuntimeShapeValid(content)) {
      console.warn('[runtime-content] Content does not appear to be valid runtime JS')
      return null
    }

    return content
  } catch (error) {
    console.warn('[runtime-content] Runtime fetch failed:', error)
    return null
  }
}

/**
 * Get runtime JavaScript content for split exports and ZIP packaging.
 */
export async function getRuntimeContent(): Promise<string> {
  if (isRuntimeShapeValid(bundledRuntimeContent) && hasFullFeatureMarkers(bundledRuntimeContent)) {
    return bundledRuntimeContent
  }

  const content = await fetchRuntimeAsset()
  if (!content) {
    throw new Error(
      'Full export runtime is unavailable. Run "npm run build:runtime" and ensure public/ascii-bg-runtime.js is served.'
    )
  }

  if (!hasFullFeatureMarkers(content)) {
    throw new Error(
      'Export runtime is incomplete and would not match the preview. Run "npm run build:runtime" to rebuild ascii-bg-runtime.js.'
    )
  }

  console.log('[runtime-content] Runtime fetched successfully, size:', content.length)
  return content
}

/**
 * Get runtime asset metadata without forcing fallback substitution.
 */
export async function getRuntimeInfo(): Promise<{
  filename: string
  version: string
  size: string
  available: boolean
}> {
  const content = await fetchRuntimeAsset()

  return {
    filename: RUNTIME_FILENAME,
    version: RUNTIME_VERSION,
    size: content ? `${(content.length / 1024).toFixed(1)} KB` : '~125 KB',
    available: Boolean(content),
  }
}
